import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// ModaleCercaSportello — ricerca filiali/sportelli con query SERVER-SIDE.
// Il dataset sportelli è grande (~96.8k), quindi non si carica tutto lato client:
// la ricerca interroga /api/anagrafica-banche/sportelli (debounced).
// Se viene passato `abi`, la ricerca è limitata a quella banca.
// ─────────────────────────────────────────────────────────────────────────────

export interface Sportello {
  abi: string;
  cab: string;
  sportello: string;
  citta?: string | null;
  cap?: string | null;
  provincia?: string | null;
}

interface Props {
  abi?: string;
  onSeleziona: (s: Sportello) => void;
  onChiudi: () => void;
}

export default function ModaleCercaSportello({ abi, onSeleziona, onChiudi }: Props) {
  const [q, setQ] = useState('');
  const [risultati, setRisultati] = useState<Sportello[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (abi) params.set('abi', abi);
        if (q.trim()) params.set('q', q.trim());
        params.set('limit', '50');
        // @api GET /api/anagrafica-banche/sportelli?abi=&q=&limit= -> {"success":"boolean","data":"Sportello[]"}
        const res = await apiFetch<{ success: boolean; data: Sportello[] }>(`/api/anagrafica-banche/sportelli?${params.toString()}`);
        setRisultati(res.data || []);
      } catch {
        setRisultati([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, abi]);

  return (
    <div className="bop-modale-cerca__overlay" onClick={(e) => e.stopPropagation()}>
      <div className="bop-modale-cerca__content" onClick={(e) => e.stopPropagation()}>
        <h3 className="bop-modale-cerca__header">
          Cerca filiale {abi ? `(ABI ${abi})` : ''}
        </h3>

        <input
          type="text"
          placeholder="Cerca per città, nome filiale o CAB..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="bop-modale-cerca__search"
          autoFocus
        />

        {loading ? (
          <div className="bop-modale-cerca__empty">Ricerca in corso…</div>
        ) : risultati.length === 0 ? (
          <div className="bop-modale-cerca__empty">
            {q || abi ? 'Nessuna filiale trovata' : 'Digita per cercare una filiale'}
          </div>
        ) : (
          <div className="bop-modale-cerca__table-scroll">
            <table className="bop-modale-cerca__table">
              <thead>
                <tr>
                  <th>ABI</th>
                  <th>CAB</th>
                  <th>Filiale</th>
                  <th>Città</th>
                  <th>Prov.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {risultati.map((s, idx) => (
                  <tr key={idx}>
                    <td className="bop-modale-cerca__cell-mono">{s.abi}</td>
                    <td className="bop-modale-cerca__cell-mono">{s.cab}</td>
                    <td>{s.sportello}</td>
                    <td>{s.citta || '—'}</td>
                    <td>{s.provincia || '—'}</td>
                    <td>
                      <button className="bop-btn-primary" onClick={() => onSeleziona(s)}>
                        Seleziona
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="bop-modale-cerca__actions">
          <button className="bop-btn-secondary" onClick={onChiudi}>
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}
